import React from 'react';
import { fmtMoney } from '../lib/format';
import { invoiceLines, invoiceAttachments } from '../lib/ledger';
import MediaStrip from './MediaStrip';
import { publicUrl } from '../lib/fileUrl';

// ── What am I paying for? ──────────────────────────────────────────────────
//
// Julian's ledger card showed one line of summary text and a number. Everything
// that answers the question — what the technician actually wrote, line by line,
// the photographs he took, the file he attached — was already on the record and
// had never been drawn on his side.
//
// This is the working view, not the document. The printed invoice names its
// attachments rather than showing them, which is right for a sheet somebody
// files; it is wrong for the moment a man is deciding whether to send $900.
// Here the pictures are the point, so they are shown.
export default function InvoiceDetail({ entry }) {
  const { items, from, estimateNumber } = invoiceLines(entry);
  const { lineFiles, docs, progressPhotos } = invoiceAttachments(entry);

  const money = (v) => fmtMoney(Number(v) || 0);
  const lineTotal = (i) => (Number(i.qty) || 1) * (Number(i.unitCost) || 0);

  return (
    <div className="idet">
      {from === 'estimate' && (
        <div className="idet-src">
          Billed against estimate {estimateNumber || ''}. These are its lines.
        </div>
      )}

      {items.length === 0 ? (
        // Not an error. Invoices raised before the line editor existed have
        // only the summary, and saying so beats an empty panel.
        <div className="idet-none">
          No line detail on this one. The description above is all the
          technician wrote.
        </div>
      ) : items.map((i, n) => (
        <div key={n} className="idet-line">
          <div className="idet-line-top">
            <span className="idet-name">{i.name || 'Untitled line'}</span>
            <span className="idet-total">{money(lineTotal(i))}</span>
          </div>

          {/* The workings, where the technician gave any. "3 hrs at $85" is
              what a person argues with; a bare $255 is what they query. */}
          {(Number(i.qty) > 1 || Number(i.unitCost) > 0) && (
            <div className="idet-maths">
              {Number(i.qty) || 1} × {money(i.unitCost)}
            </div>
          )}

          {/* His own words, in full and unclipped. This is the whole request:
              the card truncated to one line and the rest was never shown. */}
          {i.description && <p className="idet-desc">{i.description}</p>}

          {i.photos?.length > 0 && <MediaStrip urls={i.photos} label="Photos" />}

          {i.files?.length > 0 && (
            <div className="idet-files">
              {i.files.map((f, k) => (
                <a key={k} href={publicUrl(f.url)} target="_blank" rel="noopener noreferrer" className="idet-file">
                  {f.name || 'Attachment'}
                </a>
              ))}
            </div>
          )}
        </div>
      ))}

      {progressPhotos.length > 0 && (
        <div className="idet-block">
          <div className="idet-block-h">Photos from the job</div>
          <MediaStrip urls={progressPhotos} />
        </div>
      )}

      {(docs.length > 0 || lineFiles.length > 0 || entry.externalUrl) && (
        <div className="idet-block">
          <div className="idet-block-h">Attached to this invoice</div>
          {entry.externalUrl && (
            <a className="idet-file" href={withScheme(entry.externalUrl)} target="_blank" rel="noopener noreferrer">
              Their own copy of this invoice
            </a>
          )}
          {docs.map((d, k) => (
            <a key={`d${k}`} className="idet-file" href={publicUrl(d.url)} target="_blank" rel="noopener noreferrer">
              {d.name || 'Document'}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// A link typed without http:// resolves against qpshub.com and 404s. Same
// guard the printed document uses.
const withScheme = (url) => {
  const u = String(url || '').trim();
  if (!u) return '';
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
};
